import React, {useEffect, useMemo, useState} from 'react';
import {
  Button,
  ButtonWithPopout,
  Heading,
  Horizontal,
  InfoIcon,
  Link,
  OptionsIcon,
  Theme,
  ThemeName,
  Vertical,
  WarningIcon,
} from '@taopix/taopix-design-system';
import {useUserFlowState} from '../Context/UserFlow/UserFlowContext';
import {useTranslation} from 'react-i18next';
import useSettings from '../../Hooks/useSettings';
import classNames from 'classnames';
import {Common, Component, Lang, Localisation} from '@taopix/taopix-core';
import {useSelector} from 'react-redux';
import {LayoutSelector} from '../../Selectors';
import {Header} from '@taopix/taopix-core/dist/types/types';
import {UserFlowType} from '../../Facade/UserFlow/UserFlowFacade';
import {getUrl} from '../../Utils/Common';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../Experience/ExperienceProvider';

export type ProjectInfoButtonProps = {
  id: string;
  perms: Header;
  projectPrice: string;
  productName?: string;
  pageCount: number;
  projectName?: string;
  children?: any;
  changeProductFunction?: () => void;
};

const ProjectInfoButton = ({
  id,
  projectPrice,
  projectName,
  productName,
  pageCount,
  perms,
  changeProductFunction,
}: ProjectInfoButtonProps) => {
  const userFlow = useUserFlowState();
  const settings = useSettings();
  const {t} = useTranslation(['*', 'optionsandextras', 'retroprint']);
  const [optionsExtrasPopoutOpen, setOptionsExtrasPopoutOpen] = useState(false);
  const layout = useSelector(LayoutSelector);
  const [warning, setWarning] = useState<string | null>(null);
  const min = layout.retroprints ? layout.minimumprintsperproject : layout.minpages;
  const max = layout.retroprints ? layout.maximumprintsperproject : layout.maxpages;
  const ui = useUiState();
  const {experience} = useExperience();

  useEffect(() => {
    const warningStringNameSpace = layout.retroprints ? 'retroprint' : '*';
    if (pageCount < min || pageCount > max) {
      setWarning(
        t(`${warningStringNameSpace}:str_WarningPageCountLimitReached`)
          .replace('^0', String(min))
          .replace('^1', String(max))
      );
    } else {
      setWarning(null);
    }
  }, [pageCount, min, max]);

  const hasPopout =
    perms.priceDisclaimer || perms.optionsAndExtrasButton || perms.projectNameAndPageCount;

  const canShowOptionsAndExtrasButton = useMemo(() => {
    const pricing = settings.state.price.pricing;
    return (
      perms.optionsAndExtrasButton &&
      (pricing !== null && pricing.components.hasOwnProperty('PRODUCT')
        ? Component.flattenComponentTree(
            Object.values(pricing.components.PRODUCT?.children || [])
          ).filter(component => {
            return ['TAOPIXAI', 'CALENDARCUSTOMISATION'].indexOf(component.sectioncode) === -1;
          })
        : []
      ).length > 0
    );
  }, [layout.code, settings.state.price.pricing, perms.optionsAndExtrasButton]);

  const optionsAndExtrasButton = ((): JSX.Element => {
    return (
      <Button
        size={
          ui.state.isSmallScreen && experience.EDITOR.header.headerHeight === 'sm'
            ? 'small'
            : 'medium'
        }
        label={t('str_LabelOptionsAndExtras')}
        startIcon={<OptionsIcon />}
        onClick={() => {
          setOptionsExtrasPopoutOpen(false);
          userFlow.activateUserFlow(UserFlowType.OptionsAndExtras);
        }}
      />
    );
  })();

  const showProjectInfo =
    perms.projectSummary &&
    ((perms.price && settings.state.price.showPrice) ||
      perms.projectNameAndPageCount ||
      canShowOptionsAndExtrasButton);

  const langCode = Lang.getCode();
  const showPrice = perms.price && settings.state.price.showPrice;
  const showNameAndPageCount = perms.projectNameAndPageCount;
  const localisedProductName = Localisation.getLocaleString(langCode, String(productName));

  const pageCountString = (() => {
    if (showNameAndPageCount) {
      const pageCountStringNameSpace = layout.retroprints ? 'retroprint' : '*';

      return pageCount === 1
        ? t(`${pageCountStringNameSpace}:str_Label1Page`)
        : t(`${pageCountStringNameSpace}:str_LabelNPages`).replace('^0', String(pageCount));
    }
    return '';
  })();

  // Determine the parts of the button label (page count and price)
  const buttonLabelPart1 = showNameAndPageCount ? pageCountString : '';
  const buttonLabelPart2 = showPrice
    ? projectPrice
    : !showNameAndPageCount
    ? t('str_LabelProjectInfo')
    : '';
  const separator = buttonLabelPart1 !== '' && buttonLabelPart2 !== '' ? ', ' : '';
  const projectSummaryLabel = buttonLabelPart1 + separator + buttonLabelPart2;

  // Determine the button icon:
  // If there is no warning or text label, we show the Info icon
  const buttonIcon = warning ? <WarningIcon /> : !showPrice ? <InfoIcon /> : undefined;

  const buttonId = 'projectInfoButton' + id;

  return (
    <>
      {showProjectInfo && (
        <>
          {hasPopout ? (
            <>
              <ButtonWithPopout
                buttonProps={{
                  label: projectSummaryLabel,
                  hideLabel: showPrice ? false : true,
                  startIcon: buttonIcon,
                  id: buttonId,
                  buttonStyle: 'distinct',
                  labelLines: ui.state.isSmallScreen && perms.headerHeight === 'sm' ? 1 : 2,
                  labelAlignmentWhenWrapped: 'left',
                  arrow: 'down',
                  className: classNames(!showPrice && 'px-sm', 'offsetFocus'),
                  size: ui.state.isSmallScreen && perms.headerHeight === 'sm' ? 'small' : 'medium',
                }}
                popoutProps={{
                  displayMode: 'positioned',
                  anchorId: buttonId,
                  anchorOrigin: {
                    horizontal: Horizontal.Right,
                    vertical: Vertical.Bottom,
                  },
                  transformOrigin: {
                    horizontal: Horizontal.Right,
                    vertical: Vertical.Top,
                  },
                  offset: {x: 0, y: 5},
                  repositionOnResize: true,
                  theme: 'tpx-theme-header',
                  themeTip: 'Header',
                  contentTheme: ThemeName.Popouts,
                  'aria-label': t('str_LabelProjectInfo'),
                }}
              >
                <div className={'flex flex-col p-sm space-y-sm max-w-[300rem]'}>
                  {perms.projectNameAndPageCount && (
                    <>
                      {warning && (
                        <Theme name={ThemeName.Warning} className={'p-sm'}>
                          {warning}
                        </Theme>
                      )}

                      <div>
                        <Heading size={3}>{projectName}</Heading>
                        <p>{projectSummaryLabel}</p>
                      </div>

                      {perms.changeProduct && changeProductFunction ? (
                        <Button
                          buttonStyle="tertiary"
                          label={localisedProductName}
                          arrow={'down'}
                          labelAlignment="left"
                          labelAlignmentWhenWrapped="left"
                          onClick={changeProductFunction}
                        />
                      ) : (
                        <p>{localisedProductName}</p>
                      )}
                    </>
                  )}

                  {perms.optionsAndExtrasButton &&
                    canShowOptionsAndExtrasButton &&
                    optionsAndExtrasButton}

                  {perms.priceDisclaimer && (
                    <>
                      <p className={'text-sm'}>
                        {'' !== settings.state.price.disclaimer
                          ? Common.getCurrentLocaleString(settings.state.price.disclaimer, true)
                          : t('*:str_MessageTaxAndShipping')}
                        {settings.state.price.infoUrl && (
                          <>
                            <br />
                            <Link href={getUrl(settings.state.price.infoUrl)} target="_blank">
                              {t('*:str_ButtonMoreInformation')}
                            </Link>
                          </>
                        )}
                      </p>
                    </>
                  )}
                </div>
              </ButtonWithPopout>
            </>
          ) : (
            <Theme name={ThemeName.Section} className={'flex items-center px-sm h-xxl'}>
              {projectSummaryLabel}
            </Theme>
          )}
        </>
      )}
    </>
  );
};

export default ProjectInfoButton;
